import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Trash2,
  Edit3,
  MoreHorizontal,
  Pencil,
  Book,
  BookOpen,
} from "lucide-react";
import ChatInterface from "./ChatInterface";
import { useAuth } from "../contexts/AuthContext";
import {
  listConversations,
  renameConversation,
  deleteConversation,
  deleteAllConversations,
} from "../utils/conversationsApi";
import { clearAllHandoffSelections } from "../utils/reportHandoffStore";
import { clearAllClarifyAnswers } from "../utils/clarifyStore";

const displayTitle = (c) => c.title || c.snippet || "New Chat";

// Sidebar stamp: date + time the chat started (its first message). Distinguishes
// same-day chats. Sorting is by last activity (server order), not this stamp.
// Exported for tests.
export const formatChatStamp = (c) => {
  const iso = c.created_at || c.updated_at;
  if (!iso) return '';
  // The conversations API emits naive-UTC ISO (no timezone suffix); parsed
  // verbatim, JS treats it as LOCAL and the stamp shows UTC wall-clock time.
  // Anchor suffix-less timestamps as UTC so they render in the user's zone.
  const d = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const AIAnalyst = ({ isOpen, darkMode }) => {
  const { fetchWithAuth } = useAuth();

  const SIDEBAR_EXPANDED = 260;
  const SIDEBAR_COLLAPSED = 0;

  const [showMenu, setShowMenu] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: null, title: '' });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuExpandUp, setMenuExpandUp] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const confirmRef = useRef(null);

  // Server-backed history. currentChatId is a conversation id (number) or null
  // (a new unsaved chat, created lazily on first send).
  const [conversations, setConversations] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('aiSidebarExpanded');
      if (saved === 'true') setShowMenu(true);
      if (saved === 'false') setShowMenu(false);
    } catch (_) {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem('aiSidebarExpanded', String(showMenu)); } catch (_) {}
  }, [showMenu]);

  const refreshList = useCallback(() => {
    return listConversations(fetchWithAuth)
      .then((list) => setConversations(Array.isArray(list) ? list : []))
      .catch(() => { /* fail-soft: keep the current list */ });
  }, [fetchWithAuth]);

  // Load the conversation list whenever the panel opens.
  useEffect(() => {
    if (isOpen) refreshList();
  }, [isOpen, refreshList]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!deleteConfirmOpen) return;
      if (confirmRef.current && confirmRef.current.contains(e.target)) return;
      setDeleteConfirmOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [deleteConfirmOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (openMenuId === null) return;
      const clickedInsideMenu = e.target.closest('.chat-menu-dropdown') || e.target.closest('.chat-menu-button');
      if (!clickedInsideMenu) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openMenuId]);

  const createNewChat = () => {
    setCurrentChatId(null);
    setShowMenu(true);
  };

  const loadChat = (id) => {
    setCurrentChatId(id);
  };

  // First send of a new chat created a server conversation: adopt it + refresh.
  const handleConversationCreated = (id) => {
    setCurrentChatId(id);
    refreshList();
  };

  // A turn finished: refresh so title / snippet / ordering reflect the server.
  const handleConversationUpdated = () => {
    refreshList();
  };

  const startRename = (id, currentTitle) => {
    setOpenMenuId(null);
    setRenamingId(id);
    setRenameValue(currentTitle || '');
  };

  const commitRename = async (id) => {
    const title = renameValue.trim();
    setRenamingId(null);
    if (!title) return;
    try {
      await renameConversation(fetchWithAuth, id, title);
      await refreshList();
    } catch (_) {
      // fail-soft
    }
  };

  const openDeleteConfirm = (id, title) => {
    setDeleteTarget({ id, title });
    setDeleteConfirmOpen(true);
  };

  const cancelDelete = () => setDeleteConfirmOpen(false);

  const confirmDelete = async () => {
    const id = deleteTarget.id;
    setDeleteConfirmOpen(false);
    if (id == null) return;
    try {
      await deleteConversation(fetchWithAuth, id);
    } catch (_) {
      // fail-soft
    }
    if (currentChatId === id) setCurrentChatId(null);
    await refreshList();
  };

  const confirmDeleteAll = async () => {
    setDeleteAllOpen(false);
    try {
      await deleteAllConversations(fetchWithAuth);
    } catch (_) {
      // fail-soft
    }
    clearAllHandoffSelections();
    clearAllClarifyAnswers();
    setCurrentChatId(null);
    await refreshList();
  };

  if (!isOpen) return null;

  // ── Theme tokens ────────────────────────────────────────────────────────────
  const textPrimary   = darkMode ? '#FFFFFF' : '#0A0A0A';
  const mutedText     = darkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.45)';
  const dimText       = darkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.30)';
  const hairline      = darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
  const hairlineStrong= darkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.20)';
  const hoverBg       = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const activeItemBg  = darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)';
  const activeOutline = darkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)';
  const bgSurface     = darkMode ? '#141414' : '#F5F5F5';
  const dropdownBg    = darkMode ? '#141414' : '#FFFFFF';
  const dropdownBorder= darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)';
  const dropdownItem  = darkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.70)';
  const dropdownHover = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const dropdownDivider=darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page header - same pattern as Portfolio / Reports */}
      <div style={{ padding: '24px 24px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Chat history toggle - open book when expanded, closed book when collapsed. */}
            <button
              onClick={() => setShowMenu((v) => !v)}
              title={showMenu ? 'Collapse history' : 'Expand history'}
              aria-label={showMenu ? 'Collapse history' : 'Expand history'}
              style={{
                padding: '6px 8px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: mutedText,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = textPrimary; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = mutedText; }}
            >
              {showMenu ? <BookOpen size={18} /> : <Book size={18} />}
            </button>
            {/* Delete all chat history (confirmed) - disabled when there is none. */}
            <button
              onClick={() => setDeleteAllOpen(true)}
              disabled={conversations.length === 0}
              title="Delete all chat history"
              aria-label="Delete all chat history"
              style={{
                padding: '6px 8px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: mutedText,
                cursor: conversations.length === 0 ? 'default' : 'pointer',
                opacity: conversations.length === 0 ? 0.35 : 1,
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => { if (conversations.length) e.currentTarget.style.color = 'rgba(220,80,80,0.95)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = mutedText; }}
            >
              <Trash2 size={17} />
            </button>
          </div>
          {/* New chat action */}
          <button
            onClick={createNewChat}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 18px',
              borderRadius: 9999,
              border: `1px solid ${hairlineStrong}`,
              background: 'transparent',
              color: mutedText,
              fontSize: 14,
              fontFamily: 'Inter, sans-serif',
              cursor: 'pointer',
              transition: 'border-color 150ms, color 150ms',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = textPrimary;
              e.currentTarget.style.color = textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = hairlineStrong;
              e.currentTarget.style.color = mutedText;
            }}
          >
            <Edit3 size={13} />
            New chat
          </button>
        </div>
        {/* Hairline divider */}
        <div style={{ height: 1, background: hairline, marginBottom: 0 }} />
      </div>

      {/* Body: history sidebar + chat area */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
        {/* Chat history sidebar */}
        <div
          className="menu-sidebar"
          style={{
            width: showMenu ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED,
            flexShrink: 0,
            overflow: 'hidden',
            borderRight: showMenu ? `1px solid ${hairline}` : 'none',
            transition: 'width 300ms ease',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0, // let the inner list scroll instead of overflowing the page
          }}
        >
          <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
            {conversations.length === 0 && (
              <div style={{ fontSize: 13, color: dimText, padding: '6px 10px', fontFamily: 'Inter, sans-serif' }}>
                No previous chats
              </div>
            )}
            {/* Chat history list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {conversations.map((chat) => (
                <div
                  key={chat.id}
                  className="group"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    borderRadius: 6,
                    background: currentChatId === chat.id ? activeItemBg : 'transparent',
                    outline: currentChatId === chat.id ? `1px solid ${activeOutline}` : 'none',
                    outlineOffset: -1,
                    position: 'relative',
                    transition: 'background 120ms',
                  }}
                  onMouseEnter={(e) => {
                    if (currentChatId !== chat.id)
                      e.currentTarget.style.background = hoverBg;
                  }}
                  onMouseLeave={(e) => {
                    if (currentChatId !== chat.id)
                      e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {renamingId === chat.id ? (
                    <input
                      value={renameValue}
                      autoFocus
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(chat.id);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      onBlur={() => commitRename(chat.id)}
                      style={{
                        flex: 1,
                        margin: '4px 8px',
                        padding: '5px 8px',
                        borderRadius: 6,
                        border: `1px solid ${activeOutline}`,
                        background: dropdownBg,
                        color: textPrimary,
                        fontSize: 14,
                        fontFamily: 'Inter, sans-serif',
                        outline: 'none',
                      }}
                    />
                  ) : (
                    <button
                      onClick={() => loadChat(chat.id)}
                      title={displayTitle(chat)}
                      style={{
                        flex: 1,
                        textAlign: 'left',
                        padding: '7px 10px',
                        borderRadius: 6,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: currentChatId === chat.id ? textPrimary : mutedText,
                        minWidth: 0,
                      }}
                    >
                      <div style={{ fontSize: 14, fontFamily: 'Inter, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {displayTitle(chat)}
                      </div>
                      {formatChatStamp(chat) && (
                        <div style={{ fontSize: 12, color: dimText, marginTop: 2 }}>
                          {formatChatStamp(chat)}
                        </div>
                      )}
                    </button>
                  )}
                  <div style={{ position: 'relative', paddingRight: 6 }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (openMenuId === chat.id) {
                          setOpenMenuId(null);
                        } else {
                          const button = e.currentTarget;
                          const rect = button.getBoundingClientRect();
                          const sidebar = button.closest('.menu-sidebar');
                          const dropdownHeight = 120;
                          if (sidebar) {
                            const sidebarRect = sidebar.getBoundingClientRect();
                            const spaceBelow = sidebarRect.bottom - rect.bottom;
                            const spaceAbove = rect.top - sidebarRect.top;
                            setMenuExpandUp(spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight);
                          } else {
                            setMenuExpandUp(window.innerHeight - rect.bottom < dropdownHeight && rect.top >= dropdownHeight);
                          }
                          setOpenMenuId(chat.id);
                        }
                      }}
                      className="chat-menu-button"
                      style={{
                        padding: 4,
                        borderRadius: 4,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: dimText,
                        opacity: currentChatId === chat.id ? 1 : 0,
                        transition: 'opacity 150ms, color 150ms',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.color = textPrimary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = dimText;
                        if (currentChatId !== chat.id) e.currentTarget.style.opacity = '0';
                      }}
                      title="More options"
                    >
                      <MoreHorizontal size={14} />
                    </button>

                    {openMenuId === chat.id && (
                      <div
                        className="chat-menu-dropdown"
                        style={{
                          position: 'absolute',
                          right: 8,
                          [menuExpandUp ? 'bottom' : 'top']: 'calc(100% + 4px)',
                          width: 148,
                          background: dropdownBg,
                          border: `1px solid ${dropdownBorder}`,
                          borderRadius: 6,
                          overflow: 'hidden',
                          zIndex: 50,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                        }}
                      >
                        <button
                          onClick={(e) => { e.stopPropagation(); startRename(chat.id, chat.title || ''); }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 14px',
                            border: 'none',
                            background: 'transparent',
                            color: dropdownItem,
                            fontSize: 14,
                            fontFamily: 'Inter, sans-serif',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <Pencil size={14} />
                          Rename
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            openDeleteConfirm(chat.id, displayTitle(chat));
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 14px',
                            border: 'none',
                            borderTop: `1px solid ${dropdownDivider}`,
                            background: 'transparent',
                            color: 'rgba(200,60,60,0.90)',
                            fontSize: 14,
                            fontFamily: 'Inter, sans-serif',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chat area */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <ChatInterface
            darkMode={darkMode}
            conversationId={currentChatId}
            onConversationCreated={handleConversationCreated}
            onConversationUpdated={handleConversationUpdated}
          />
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmOpen && (
        <>
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 60,
              background: 'rgba(0,0,0,0.6)',
            }}
            onClick={cancelDelete}
          />
          <div
            ref={confirmRef}
            style={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 70,
              width: 320,
              background: bgSurface,
              border: `1px solid ${hairline}`,
              borderRadius: 6,
              boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{ padding: '20px 24px' }}>
              <h3 style={{ fontSize: 16, fontWeight: 500, color: textPrimary, margin: '0 0 10px', fontFamily: 'Inter, sans-serif' }}>
                Delete chat?
              </h3>
              <p style={{ fontSize: 14, color: mutedText, lineHeight: 1.6, margin: '0 0 20px', fontFamily: 'Inter, sans-serif' }}>
                This will remove "{deleteTarget.title}" from your history.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={cancelDelete}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 9999,
                    border: `1px solid ${hairlineStrong}`,
                    background: 'transparent',
                    color: mutedText,
                    fontSize: 14,
                    fontFamily: 'Inter, sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 9999,
                    border: 'none',
                    background: 'rgba(220,50,50,0.85)',
                    color: '#FFFFFF',
                    fontSize: 14,
                    fontFamily: 'Inter, sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete ALL chat history confirmation modal */}
      {deleteAllOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.6)' }}
            onClick={() => setDeleteAllOpen(false)}
          />
          <div
            style={{
              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 70, width: 340, background: bgSurface, border: `1px solid ${hairline}`,
              borderRadius: 6, boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{ padding: '20px 24px' }}>
              <h3 style={{ fontSize: 16, fontWeight: 500, color: textPrimary, margin: '0 0 10px', fontFamily: 'Inter, sans-serif' }}>
                Delete all chat history?
              </h3>
              <p style={{ fontSize: 14, color: mutedText, lineHeight: 1.6, margin: '0 0 20px', fontFamily: 'Inter, sans-serif' }}>
                This removes all {conversations.length} chat{conversations.length === 1 ? '' : 's'} from your history. This can't be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={() => setDeleteAllOpen(false)}
                  style={{ padding: '7px 16px', borderRadius: 9999, border: `1px solid ${hairlineStrong}`, background: 'transparent', color: mutedText, fontSize: 14, fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAll}
                  style={{ padding: '7px 16px', borderRadius: 9999, border: 'none', background: 'rgba(220,50,50,0.85)', color: '#FFFFFF', fontSize: 14, fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}
                >
                  Delete all
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAnalyst;
